import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Member } from '@gem/call/CallStore/Member/Member';
import {
  ICallPersonalizedPermissions,
  PermissionsPersonalizedValue,
  PermissionsTypes,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { useTranslation } from '@gem/modules/i18n';
import { PopupStore, popupsTasks } from '@gem/popups';

import { CallMemberPermissionsPopup } from '../../../CallMemberPermissionsPopup/CallMemberPermissionsPopup';
import { ICallMemberPermissionsItemProps } from './CallMemberPermissionsItem.types';

const permissionsTranslations = {
  [PermissionsTypes.SwitchOnCamera]: {
    [PermissionsPersonalizedValue.Always]: 'call_permissions_custom_camera_always',
    [PermissionsPersonalizedValue.Never]: 'call_permissions_custom_camera_never',
    [PermissionsPersonalizedValue.AfterApprove]: 'call_permissions_custom_camera_after_approval',
  },
  [PermissionsTypes.SwitchOnMic]: {
    [PermissionsPersonalizedValue.Always]: 'call_permissions_custom_mic_always',
    [PermissionsPersonalizedValue.Never]: 'call_permissions_custom_mic_never',
    [PermissionsPersonalizedValue.AfterApprove]: 'call_permissions_custom_mic_after_approval',
  },
  [PermissionsTypes.ShareScreen]: {
    [PermissionsPersonalizedValue.Always]: 'call_permissions_sharing_always',
    [PermissionsPersonalizedValue.Never]: 'call_permissions_sharing_never',
    [PermissionsPersonalizedValue.AfterApprove]: 'call_permissions_sharing_after_approval',
  },
};

interface ICallMemberPermissionsItemPresenter {
  permissionsTranslations: string;
  member: Member | null;
  permissions: ICallPersonalizedPermissions;
  childPopup: PopupStore | null;
  userId: string;
  onMemberClick(): void;
  onClose(): void;
}

export const useCallMemberPermissionsItemPresenter = (
  props: ICallMemberPermissionsItemProps
): ICallMemberPermissionsItemPresenter => {
  const { t } = useTranslation();

  const presenter = useLocalObservable<ICallMemberPermissionsItemPresenter>(() => ({
    childPopup: null,
    userId: props.userId,

    get permissions(): ICallPersonalizedPermissions {
      const personalizedPermissions = props.call.permissions.customPermissions.get(
        presenter.userId
      );

      return props.call.permissions.getMemberPersonalizedPermissions(personalizedPermissions || {});
    },

    get member(): Member | null {
      return props.call.members.getMemberById(presenter.userId);
    },

    get permissionsTranslations(): string {
      return Object.values(PermissionsTypes)
        .map((permission) => {
          const permissionValue = presenter.permissions[permission];

          if (!permissionValue) {
            return '';
          }

          return t(permissionsTranslations[permission][permissionValue]);
        })
        .join(', ');
    },

    onClose(): void {
      props.onClose?.();
    },

    onMemberClick(): void {
      props.call.permissions.setEditedPermissions(presenter.permissions, presenter.userId);
      props.popupInstance.hide();
      presenter.childPopup = popupsTasks.openPopup(CallMemberPermissionsPopup, {
        onClose: () => presenter.onClose(),
        onBack: () => props.popupInstance.show(),
        params: {
          handleBackToGeneral: props.popupInstance.show,
          userId: presenter.userId,
          callController: props.callController,
          call: props.call,
        },
      });
    },
  }));

  useEffect(() => {
    presenter.userId = props.userId;
  }, [props.userId]);

  return presenter;
};
